import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Card, Col, Container, Form, Row, Table } from "react-bootstrap";
import Select from "react-select";
import AddButton from "components/ui/AddButton";
import ActionButtons from "components/ui/ActionButtons";
import CommonModal from "components/ui/CommonModal";
import { GRADIENTS } from "utils/theme";
import Useaxios from "utils/Useaxios";
import { API } from "utils/Apiroutes";
import axiosInstance from "utils/AxiosInstance";
import "./creative-video-assignments.css";
import TableFilter from "components/ui/TableFilter";
import LightGallery from 'lightgallery/react';
import lgZoom from 'lightgallery/plugins/zoom';
import lgVideo from 'lightgallery/plugins/video';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import { imageUrl } from "utils/helper";

const STORAGE_KEY = "creative_video_machine_assignments";

const normalizeAssignments = (rawData) => {
  if (!Array.isArray(rawData)) return [];

  return rawData.map((item, index) => {
    const machineIds = Array.isArray(item.machineIds)
      ? item.machineIds
      : Array.isArray(item.assignedMachines)
        ? item.assignedMachines
        : item.machineId
          ? [item.machineId]
          : [];

    const machineNames = Array.isArray(item.machineNames)
      ? item.machineNames
      : item.machineName
        ? [item.machineName]
        : machineIds.map((id) => id);
    const mediaUrls = toMediaUrlArray(item.mediaUrl, item.videoUrl);

    return {
      id: item.id || item._id || `video-${Date.now()}-${index}`,
      title: item.title || "Untitled Video",
      fileName: item.fileName || getFileNamesFromUrls(mediaUrls).join(", ") || "unknown-file.mp4",
      fileSize: item.fileSize || 0,
      uploadedAt: item.uploadedAt || item.createdAt || new Date().toISOString(),
      machineIds,
      machineNames,
      mediaUrls,
      videoUrl: mediaUrls[0] || "",
      mediaType: item.mediaType || item.type || "video"
    };
  });
};
const getFileNameFromUrl = (mediaUrl) => {
  if (!mediaUrl) return "";

  const url = Array.isArray(mediaUrl) ? mediaUrl[0] : mediaUrl;
  if (typeof url !== "string") return "";

  return url?.split("/").pop();
};

const toMediaUrlArray = (mediaUrl, videoUrl) => {
  const sources = [];

  if (Array.isArray(mediaUrl)) {
    sources.push(...mediaUrl);
  } else if (typeof mediaUrl === "string" && mediaUrl) {
    sources.push(mediaUrl);
  }

  if (Array.isArray(videoUrl)) {
    sources.push(...videoUrl);
  } else if (typeof videoUrl === "string" && videoUrl) {
    sources.push(videoUrl);
  }

  return sources.filter((url) => typeof url === "string" && url.trim().length > 0);
};

const getFileNamesFromUrls = (urls = []) => {
  return urls.map((url) => getFileNameFromUrl(url)).filter(Boolean);
};

const IMAGE_EXTENSIONS = [".png", ".jpg", ".jpeg", ".gif", ".webp", ".bmp", ".svg", ".avif"];
const VIDEO_EXTENSIONS = [".mp4", ".webm", ".ogg", ".mov", ".m4v", ".avi", ".mkv"];

const isAllowedFileForType = (file, mediaType) => {
  const mimeType = (file?.type || "").toLowerCase();
  const fileName = (file?.name || "").toLowerCase();

  if (mediaType === "image") {
    if (mimeType.startsWith("image/")) return true;
    return IMAGE_EXTENSIONS.some((ext) => fileName.endsWith(ext));
  }

  if (mediaType === "video") {
    if (mimeType.startsWith("video/")) return true;
    return VIDEO_EXTENSIONS.some((ext) => fileName.endsWith(ext));
  }

  return false;
};

const isVideoMediaUrl = (url, fallbackType = "") => {
  if (!url || typeof url !== "string") return fallbackType === "video";
  const cleanUrl = url.split("?")[0].toLowerCase();
  if (/\.(mp4|webm|ogg|mov|m4v|avi|mkv)$/.test(cleanUrl)) return true;
  if (/\.(png|jpe?g|gif|webp|bmp|svg|avif)$/.test(cleanUrl)) return false;
  return fallbackType === "video";
};

const resolveMediaUrl = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;

  try {
    return new URL(url, axiosInstance.defaults.baseURL).toString();
  } catch {
    return url;
  }
};

function CreativeVideoAssignments() {
  const { fetchData } = Useaxios();
  const fileInputRef = useRef(null);

  const [machines, setMachines] = useState([]);
  const [loadingMachines, setLoadingMachines] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [pageError, setPageError] = useState("");
  const [modalError, setModalError] = useState("");
  const [searchText, setSearchText] = useState("");
  const [machineFilter, setMachineFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");

  const [showManageModal, setShowManageModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingAssignmentId, setEditingAssignmentId] = useState(null);
  const [viewingAssignment, setViewingAssignment] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    mediaType: "video",
    videoFile: null,
    videoFiles: [],
    existingMediaUrls: [],
    originalMediaUrls: [],
    fileName: "",
    fileSize: 0,
    machineSelections: []
  });

  const [assignments, setAssignments] = useState(() => {
    try {
      const savedData = localStorage.getItem(STORAGE_KEY);
      return normalizeAssignments(savedData ? JSON.parse(savedData) : []);
    } catch {
      return [];
    }
  });

  const machineOptions = useMemo(() => {
    return machines.map((machine) => ({
      value: machine.machineId,
      label: `${machine.name} (${machine.machineId})`
    }));
  }, [machines]);

  const machineNameMap = useMemo(() => {
    return machines.reduce((acc, machine) => {
      acc[machine.machineId] = machine.name;
      return acc;
    }, {});
  }, [machines]);

  const getMachineName = (machineId, fallbackName = "") => {
    return machineNameMap[machineId] || fallbackName || machineId;
  };

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(assignments));
  }, [assignments]);

  useEffect(() => {
    const getMachines = async () => {
      setLoadingMachines(true);
      try {
        const res = await fetchData({
          url: API.getMachine,
          method: "GET",
          showMessage: false,
          params: { limit: 1000 }
        });

        if (res?.success) {
          setMachines(res?.data?.data || []);
        }
      } catch {
        setPageError("Failed to load machines. Please refresh and try again.");
      } finally {
        setLoadingMachines(false);
      }
    };

    const getVideos = async () => {
      try {
        const res = await fetchData({
          url: API.getVideos,
          method: "GET",
          showMessage: false,
          params: { page: 1, limit: 1000 }
        });

        if (res?.success) {
          setAssignments(normalizeAssignments(res?.data?.data || []));
        }
      } catch {
        setPageError("Failed to load videos. Please refresh and try again.");
      }
    };

    getMachines();
    getVideos();
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  useEffect(() => {
    if (machines.length === 0) return;

    setAssignments((prev) => {
      return prev.map((item) => {
        const updatedNames = item.machineIds.map((machineId) => {
          const foundMachine = machines.find((machine) => machine.machineId === machineId);
          return foundMachine?.name || machineId;
        });
        return { ...item, machineNames: updatedNames };
      });
    });
  }, [machines]);

  const totalVideos = assignments.length;
  const totalMachines = new Set(assignments.flatMap((item) => item.machineIds)).size;

  const filterFields = [
    {
      key: 'search',
      label: 'Search',
      type: 'text',
      placeholder: 'Search by video title, file or machine...'
    },
    {
      key: 'machineId',
      label: 'Machine',
      type: 'select',
      options: [
        // { value: '', label: 'All Machines' },
        ...machines.map(m => ({ value: m.machineId, label: `${m.name} (${m.machineId})` }))
      ]
    }
  ];

  const activeFilterCount = [searchText, machineFilter].filter(Boolean).length;

  const filteredAssignments = useMemo(() => {
    return assignments.filter((item) => {
      const text = searchText.trim().toLowerCase();
      const machineText = item.machineIds
        .map((machineId, idx) => `${getMachineName(machineId, item.machineNames?.[idx])} ${machineId}`)
        .join(" ")
        .toLowerCase();
      const matchesSearch =
        !text ||
        item.title.toLowerCase().includes(text) ||
        item.fileName.toLowerCase().includes(text) ||
        machineText.includes(text);

      const matchesMachine = !machineFilter || item.machineIds.includes(machineFilter);
      return matchesSearch && matchesMachine;
    });
  }, [assignments, searchText, machineFilter, machineNameMap]);

  const formatFileSize = (bytes) => {
    if (!bytes) return "N/A";
    const units = ["B", "KB", "MB", "GB"];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex += 1;
    }
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  const resetForm = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl("");
    setModalError("");
    setFormData({
      title: "",
      mediaType: "video",
      videoFile: null,
      videoFiles: [],
      existingMediaUrls: [],
      originalMediaUrls: [],
      fileName: "",
      fileSize: 0,
      machineSelections: []
    });
  };

  const closeManageModal = () => {
    setShowManageModal(false);
    setIsEditMode(false);
    setEditingAssignmentId(null);
    resetForm();
  };

  const handleOpenCreate = () => {
    setIsEditMode(false);
    setEditingAssignmentId(null);
    resetForm();
    setShowManageModal(true);
  };

  const handleOpenEdit = (assignment) => {
    const selectedMachines = assignment.machineIds.map((machineId) => {
      const fromOptions = machineOptions.find((option) => option.value === machineId);
      return (
        fromOptions || {
          value: machineId,
          label: `${getMachineName(machineId)} (${machineId})`
        }
      );
    });

    setIsEditMode(true);
    setEditingAssignmentId(assignment.id);
    setModalError("");

    setFormData({
      title: assignment.title,
      mediaType: assignment.mediaType || "video",
      videoFile: null,
      videoFiles: [],
      existingMediaUrls: assignment.mediaUrls?.length > 0
        ? assignment.mediaUrls
        : assignment.videoUrl
          ? [assignment.videoUrl]
          : [],
      originalMediaUrls: assignment.mediaUrls?.length > 0
        ? assignment.mediaUrls
        : assignment.videoUrl
          ? [assignment.videoUrl]
          : [],
      fileName: assignment.fileName,
      fileSize: assignment.fileSize,
      machineSelections: selectedMachines
    });

    setShowManageModal(true);
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    const validFiles = files.filter((file) => isAllowedFileForType(file, formData.mediaType));

    setFormData((prev) => {
      const existingFiles = prev.videoFiles || [];
      const existingKeys = new Set(
        existingFiles.map((file) => `${file.name}-${file.size}-${file.lastModified}`)
      );

      const newUniqueFiles = validFiles.filter(
        (file) => !existingKeys.has(`${file.name}-${file.size}-${file.lastModified}`)
      );
      const mergedFiles = [...existingFiles, ...newUniqueFiles];

      return {
        ...prev,
        videoFiles: mergedFiles,
        fileName: mergedFiles.map((f) => f.name).join(", "),
        fileSize: mergedFiles.reduce((acc, f) => acc + f.size, 0)
      };
    });

    if (validFiles.length !== files.length) {
      setModalError(
        formData.mediaType === "image"
          ? "Only image files are allowed when type is Image."
          : "Only video files are allowed when type is Video."
      );
    } else {
      setModalError("");
    }
    event.target.value = "";
  };

  const handleAddMoreFiles = () => {
    fileInputRef.current?.click();
  };

  const removeSelectedFile = (indexToRemove) => {
    setFormData((prev) => {
      const updatedFiles = (prev.videoFiles || []).filter((_, index) => index !== indexToRemove);
      return {
        ...prev,
        videoFiles: updatedFiles,
        fileName: updatedFiles.map((file) => file.name).join(", "),
        fileSize: updatedFiles.reduce((acc, file) => acc + file.size, 0)
      };
    });
  };

  const clearSelectedFiles = () => {
    setFormData((prev) => ({
      ...prev,
      videoFiles: [],
      fileName: prev.existingMediaUrls?.length > 0 ? prev.fileName : "",
      fileSize: prev.existingMediaUrls?.length > 0 ? prev.fileSize : 0
    }));
  };

  const removeExistingMedia = (indexToRemove) => {
    setFormData((prev) => ({
      ...prev,
      existingMediaUrls: (prev.existingMediaUrls || []).filter((_, index) => index !== indexToRemove)
    }));
  };



  const handleSubmit = async (event) => {
    event.preventDefault();
    setModalError("");

    if (!formData.title.trim()) {
      setModalError("Video title is required.");
      return;
    }
    const hasInvalidSelectedFiles = (formData.videoFiles || []).some(
      (file) => !isAllowedFileForType(file, formData.mediaType)
    );
    if (hasInvalidSelectedFiles) {
      setModalError(
        formData.mediaType === "image"
          ? "Only image files are allowed when type is Image."
          : "Only video files are allowed when type is Video."
      );
      return;
    }
    if (formData.machineSelections.length === 0) {
      setModalError("Please assign at least one machine.");
      return;
    }
    if (!isEditMode && !formData.videoFiles?.length) {
      setModalError("Please upload at least one media file.");
      return;
    }
    if (isEditMode && !formData.originalMediaUrls?.length && !formData.videoFiles.length) {
      setModalError("Please upload at least one media file.");
      return;
    }

    const selectedMachineIds = formData.machineSelections.map((machine) => machine.value);
    setSubmitLoading(true);
    try {
      if (isEditMode) {
        const payload = new FormData();
        payload.append("title", formData.title.trim());
        payload.append("type", formData.mediaType);

        // Send existing media URLs that user wants to keep
        if (formData.existingMediaUrls?.length > 0) {
          formData.existingMediaUrls.forEach((url) => {
            payload.append("video", url);
          });
        }

        // Send new files if any
        if (formData.videoFiles?.length > 0) {
          formData.videoFiles.forEach((file) => {
            payload.append("video", file);
          });
        }
        
        selectedMachineIds.forEach((machineId) => payload.append("machineIds", machineId));

        const res = await fetchData({
          url: API.updateVideo(editingAssignmentId),
          method: "PUT",
          data: payload,
          headers: { "Content-Type": "multipart/form-data" }
        });

        console.log("Update response:", res);

        if (!res?.success) {
          throw new Error(res?.message || "Video update failed");
        }

        const apiVideo = res?.data || {};

        const updatedMachineIds = Array.isArray(apiVideo.assignedMachines) ? apiVideo.assignedMachines : selectedMachineIds;
        const updatedMachineNames = updatedMachineIds.map(id => getMachineName(id));

        const updatedMediaUrls = toMediaUrlArray(apiVideo.mediaUrl, apiVideo.videoUrl);
        const fallbackUploadedNames = formData.videoFiles?.map((file) => file.name).join(", ");
        const fallbackExistingNames = getFileNamesFromUrls(formData.existingMediaUrls || []).join(", ");

        setAssignments(prev =>
          prev.map(item =>
            item.id === editingAssignmentId
              ? {
                ...item,
                title: apiVideo.title || formData.title.trim(),
                fileName: getFileNamesFromUrls(updatedMediaUrls).join(", ") || fallbackUploadedNames || fallbackExistingNames || item.fileName,
                machineIds: [...updatedMachineIds],
                machineNames: [...updatedMachineNames],
                mediaUrls: updatedMediaUrls.length > 0 ? [...updatedMediaUrls] : formData.existingMediaUrls || [],
                videoUrl: updatedMediaUrls[0] || formData.existingMediaUrls?.[0] || "",
                mediaType: apiVideo.type || formData.mediaType,
                uploadedAt: apiVideo.updatedAt || item.uploadedAt
              }
              : item
          )
        );
      } else {
        const payload = new FormData();
        payload.append("title", formData.title.trim());
        payload.append("type", formData.mediaType);
        if (formData.videoFiles?.length > 0) {
          formData.videoFiles.forEach(file => {
            payload.append("video", file);
          });
        }
        selectedMachineIds.forEach((machineId) => payload.append("machineIds", machineId));

        const res = await fetchData({
          url: API.createVideo,
          method: "POST",
          data: payload,
          headers: { "Content-Type": "multipart/form-data" }
        });

        if (!res?.success) {
          throw new Error(res?.message || "Video upload failed");
        }

        const apiVideo = res?.data || {};
        const assignedMachineIds = Array.isArray(apiVideo.assignedMachines) ? apiVideo.assignedMachines : selectedMachineIds;
        const assignedMachineNames = assignedMachineIds.map((machineId) => {
          return getMachineName(machineId);
        });
        const createdMediaUrls = toMediaUrlArray(apiVideo.mediaUrl, apiVideo.videoUrl);
        const fallbackUploadedNames = formData.videoFiles?.map((file) => file.name).join(", ");

        const apiFileName =
          getFileNamesFromUrls(createdMediaUrls).join(", ") ||
          fallbackUploadedNames ||
          "uploaded-video";

        const newAssignment = {
          id: apiVideo._id || `video-${Date.now()}`,
          title: apiVideo.title || formData.title.trim(),
          fileName: apiFileName,
          fileSize: formData.fileSize,
          uploadedAt: apiVideo.createdAt || new Date().toISOString(),
          machineIds: assignedMachineIds,
          machineNames: assignedMachineNames,
          mediaUrls: createdMediaUrls,
          videoUrl: createdMediaUrls[0] || "",
          mediaType: apiVideo.type || formData.mediaType
        };
        setAssignments((prev) => [newAssignment, ...prev]);
      }
      closeManageModal();
    } catch (error) {
      setModalError(error?.response?.data?.message || error?.message || "Failed to save video assignment.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = (assignment) => {
    const Swal = require("sweetalert2");
    Swal.fire({
      title: "Delete Assignment?",
      text: "This Creative Management assignment will be removed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it"
    }).then(async (result) => {
      if (result.isConfirmed) {
        setSubmitLoading(true);
        try {
          await fetchData({
            url: API.deleteVideo(assignment.id),
            method: "DELETE",
            showMessage: false
          });
          setAssignments((prev) => prev.filter((item) => item.id !== assignment.id));
        } catch (error) {
          setPageError(error?.response?.data?.message || error?.message || "Failed to delete video assignment.");
        } finally {
          setSubmitLoading(false);
        }
      }
    });
  };
  
  const handleOpenView = (assignment) => {
  setViewingAssignment(assignment);
  setShowViewModal(true);
};
  return (
    <>
      <Container fluid className="py-4 creative-page">
        <Row className="mb-4">
          <Col md={12}>
            <Card className="shadow border-0 rounded-3 overflow-hidden">
              <div className="text-white py-3 px-3" style={{ background: GRADIENTS.primary }}>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <div>
                    <h4 className="mb-1">
                      <i className="fas fa-photo-video me-2"></i>
                      Creative Management
                    </h4>
                    <p className="mb-0 opacity-75">
                      Manage creative content, assign to multiple machines, and track deployments.
                    </p>
                  </div>
                  <div className="d-flex gap-2 flex-wrap">
                    <Button
                      variant="light"
                      size="sm"
                      onClick={() => setShowFilters(!showFilters)}
                      className="fw-medium d-flex align-items-center"
                    >
                      <i className="fas fa-filter me-1"></i>
                      <span className="d-none d-md-inline">Filters</span>
                      {activeFilterCount > 0 && (
                        <Badge bg="danger" className="ms-2">{activeFilterCount}</Badge>
                      )}
                    </Button>
                    <AddButton onClick={handleOpenCreate} icon="fas fa-plus-circle">
                      Add Creative
                    </AddButton>
                  </div>
                </div>
              </div>

              {showFilters && (
                <div className="px-4 pt-3 border-bottom">
                  <TableFilter
                    filterConfig={filterFields}
                    filters={{ search: searchText, machineId: machineFilter }}
                    onFilterChange={(newFilters) => {
                      if ('search' in newFilters) setSearchText(newFilters.search);
                      if ('machineId' in newFilters) setMachineFilter(newFilters.machineId);
                    }}
                    onClearFilters={() => {
                      setSearchText('');
                      setMachineFilter('');
                    }}
                  />
                </div>
              )}

              <Card.Body className="p-3 p-md-4">
                {pageError && <Alert variant="danger">{pageError}</Alert>}

                <div className="p-4 bg-light border-bottom">
                  <Row className="g-3">
                    <Col md={4}>
                      <div className="stat-card">
                        <div className="d-flex align-items-center">
                          <div className="stat-icon bg-primary bg-opacity-10 text-primary rounded-circle p-3 me-3">
                            <i className="fas fa-video fa-lg"></i>
                          </div>
                          <div>
                            <h3 className="fw-bold mb-0">{totalVideos}</h3>
                            <small className="text-muted">Total Creatives</small>
                          </div>
                        </div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="stat-card">
                        <div className="d-flex align-items-center">
                          <div className="stat-icon bg-success bg-opacity-10 text-success rounded-circle p-3 me-3">
                            <i className="fas fa-robot fa-lg"></i>
                          </div>
                          <div>
                            <h3 className="fw-bold mb-0">{totalMachines}</h3>
                            <small className="text-muted">Machines Covered</small>
                          </div>
                        </div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="stat-card">
                        <div className="d-flex align-items-center">
                          <div className="stat-icon bg-info bg-opacity-10 text-info rounded-circle p-3 me-3">
                            <i className="fas fa-database fa-lg"></i>
                          </div>
                          <div>
                            <h3 className="fw-bold mb-0">{machines.length}</h3>
                            <small className="text-muted">Live Machines</small>
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>

                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    {filteredAssignments.length === 0 ? (
                      <div className="empty-list">
                        <i className="fas fa-video mb-2"></i>
                        <p className="mb-0 text-muted">No creative content found.</p>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <Table hover className="mb-0 align-middle modern-table">
                          <thead className="table-light">
                            <tr>
                              <th style={{width: '35%'}}>Creative</th>
                              <th style={{width: '25%'}}>Machines</th>
                              <th style={{width: '15%', whiteSpace: 'nowrap'}}>Assigned On</th>
                              <th className="text-center" style={{width: '12%'}}>Status</th>
                              <th className="text-center" style={{width: '13%'}}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredAssignments.map((item) => {
                              const imageUrls = item.mediaUrls.filter(url => !isVideoMediaUrl(url, item.mediaType));
                              const hasVideo = item.mediaUrls.some(url => isVideoMediaUrl(url, item.mediaType));

                              return (
                              <tr key={item.id} className="table-row-hover">
                                <td>
                                  <div className="d-flex align-items-center gap-3">
                                    <div className="creative-preview-wrapper">
                                      {imageUrls.length > 0 ? (
                                        <div className="creative-thumbnail-stack">
                                          <img 
                                            src={imageUrl(imageUrls[0])} 
                                            alt={item.title}
                                            className="creative-thumbnail"
                                          />
                                          {imageUrls.length > 1 && (
                                            <div className="thumbnail-count-badge">
                                              +{imageUrls.length - 1}
                                            </div>
                                          )}
                                        </div>
                                      ) : hasVideo ? (
                                        <div className="creative-thumbnail video-placeholder">
                                          <i className="fas fa-play-circle fa-2x text-primary"></i>
                                          {item.mediaUrls.length > 1 && (
                                            
                                         <div className="thumbnail-count-badge">
                                              +{item.mediaUrls.length - 1}
                                            </div>
                            )}
                                        </div>
                                      ) : (
                                        <div className="creative-thumbnail placeholder">
                                          <i className="fas fa-image text-muted"></i>
                                        </div>
                                      )}
                                    </div>
                                    <div className="creative-info">
                                      <div className="fw-bold text-dark mb-1">{item.title}</div>
                                      <div className="d-flex align-items-center gap-2 flex-wrap">
                                        {hasVideo && (
                                          <Badge bg="danger" className="badge-sm">
                                            <i className="fas fa-video me-1"></i>
                                            Video
                                          </Badge>
                                        )}
                                        {imageUrls.length > 0 && (
                                          <Badge bg="info" className="badge-sm">
                                            <i className="fas fa-images me-1"></i>
                                            {imageUrls.length} Image{imageUrls.length > 1 ? 's' : ''}
                                          </Badge>
                                        )}
                                        
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <div className="machine-badges-container">
                                    {item.machineIds.slice(0, 2).map((machineId, index) => (
                                      <div className="machine-badge-item" key={`${item.id}-${machineId}-${index}`}>
                                        <Badge bg="light" text="dark" className="border px-2 py-1">
                                          <i className="fas fa-microchip me-1 text-success"></i>
                                          {getMachineName(machineId, item.machineNames?.[index])}
                                        </Badge>
                                      </div>
                                    ))}
                                    {item.machineIds.length > 2 && (
                                      <Badge bg="secondary" className="px-2 py-1">
                                        <i className="fas fa-plus me-1"></i>
                                        {item.machineIds.length - 2} more
                                      </Badge>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <div className="date-info">
                                    <div className="fw-medium text-dark">
                                      {new Date(item.uploadedAt).toLocaleDateString('en-US', { 
                                        month: 'short', 
                                        day: 'numeric', 
                                        year: 'numeric' 
                                      })}
                                    </div>
                                    <small className="text-muted">
                                      {new Date(item.uploadedAt).toLocaleTimeString('en-US', { 
                                        hour: '2-digit', 
                                        minute: '2-digit' 
                                      })}
                                    </small>
                                  </div>
                                </td>
                                <td className="text-center">
                                  <Badge bg="success" className="status-badge px-3 py-2">
                                    <i className="fas fa-check-circle me-1"></i>
                                    Active
                                  </Badge>
                                </td>
                                <td className="text-center">
                                  <ActionButtons
                                    onEdit={() => handleOpenEdit(item)}
                                    onDelete={() => handleDelete(item)}
                                    onView={() => handleOpenView(item)}
                                    showView={true}
                                  />
                                </td>
                              </tr>
                            );})}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container >

      <CommonModal
        visible={showManageModal}
        onClose={closeManageModal}
        title={
          <div className="d-flex align-items-center text-white">
            <div className="modal-icon bg-white bg-opacity-20 rounded-circle p-2 me-3">
              <i className={`fas ${isEditMode ? "fa-edit" : "fa-photo-video"} fa-lg`}></i>
            </div>
            <div>
              <h5 className="mb-0">{isEditMode ? "Edit Creative Assignment" : "Assign Creative Content"}</h5>
              <small className="opacity-75">
                {isEditMode ? "Update machines for this creative" : "Upload creative content and assign to machines"}
              </small>
            </div>
          </div>
        }
        size="lg"
        showFooter={false}
        backdrop="static"
        customHeaderClass="modal-header-gradient"
      >
        <Form onSubmit={handleSubmit}>
          {modalError && <Alert variant="danger">{modalError}</Alert>}

          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  <i className="fas fa-heading me-2 text-primary"></i>Creative Title
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. Summer Offer Campaign"
                  value={formData.title}
                  onChange={(event) => setFormData((prev) => ({ ...prev, title: event.target.value }))}
                  className="form-control-custom"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  <i className="fas fa-layer-group me-2 text-primary"></i>Content Type
                </Form.Label>
                <Form.Select
                  value={formData.mediaType}
                  disabled={isEditMode}
                  onChange={(event) => {
                    if (previewUrl) {
                      URL.revokeObjectURL(previewUrl);
                      setPreviewUrl("");
                    }
                    setFormData((prev) => ({
                      ...prev,
                      mediaType: event.target.value,
                      videoFile: null,
                      videoFiles: [],
                      fileName: "",
                      fileSize: 0
                    }));
                  }}
                  className="form-control-custom"
                >
                  <option value="video">Video</option>
                  <option value="image">image</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  <i className="fas fa-cogs me-2 text-primary"></i>Assign Machines
                </Form.Label>
                <Select
                  isMulti
                  options={machineOptions}
                  value={formData.machineSelections}
                  onChange={(selected) => {
                    setFormData((prev) => ({ ...prev, machineSelections: selected || [] }));
                  }}
                  className="react-select-custom"
                  classNamePrefix="react-select"
                  placeholder="Select one or more machines..."
                />
                <small className="text-muted d-block mt-2">
                  {formData.machineSelections.length} machine(s) selected
                </small>
              </Form.Group>
            </Col>
          </Row>

          <div className="mt-3">
            <Form.Group>
              <Form.Label className="fw-semibold">
                <i className="fas fa-file-upload me-2 text-primary"></i>
                Creative Files
              </Form.Label>
              <Form.Control
                ref={fileInputRef}
                type="file"
                multiple
                accept={formData.mediaType === "image" ? "image/*" : "video/*"}
                onChange={handleFileChange}
                style={{display: "none"}}
              />
              <div className="d-flex gap-2 mt-2 flex-wrap">
                <Button type="button" variant="outline-primary" size="sm" onClick={handleAddMoreFiles}>
                  <i className="fas fa-plus me-1"></i>
                  Add  Files
                </Button>
                {formData.videoFiles?.length > 0 && (
                  <Button type="button" variant="outline-danger" size="sm" onClick={clearSelectedFiles}>
                    <i className="fas fa-trash me-1"></i>
                    Clear All
                  </Button>
                )}
              </div>
              <small className="text-muted d-block mt-2">
                {formData.videoFiles?.length > 0
                  ? `${formData.videoFiles.length} file(s) selected`
                  : isEditMode && formData.existingMediaUrls?.length > 0
                    ? "Current media loaded. Add files to replace."
                  : "No file selected"}
              </small>
              {formData.videoFiles?.length > 0 && (
                <div className="selected-files-list">
                  {formData.videoFiles.map((file, index) => (
                    <div className="selected-file-item" key={`${file.name}-${file.lastModified}-${index}`}>
                      <span className="selected-file-name">{file.name}</span>
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="selected-file-remove"
                        onClick={() => removeSelectedFile(index)}
                      >
                        <i className="fas fa-times"></i>
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </Form.Group>
          </div>
          {isEditMode && formData.existingMediaUrls?.length > 0 && (
            <div className="mt-3">
              <h6 className="mb-2">Current Creative</h6>
              <div className="preview-panel">
                {formData.existingMediaUrls.map((url, index) =>
                  isVideoMediaUrl(url, formData.mediaType) ? (
                    <div className="preview-item" key={`existing-${index}`}>
                      <Button
                        type="button"
                        variant="light"
                        size="sm"
                        className="preview-remove-btn"
                        onClick={() => removeExistingMedia(index)}
                      >
                        <i className="fas fa-times"></i>
                      </Button>
                      <video controls src={resolveMediaUrl(url)} className="video-preview" />
                    </div>
                  ) : (
                    <div className="preview-item" key={`existing-${index}`}>
                      <Button
                        type="button"
                        variant="light"
                        size="sm"
                        className="preview-remove-btn"
                        onClick={() => removeExistingMedia(index)}
                      >
                        <i className="fas fa-times"></i>
                      </Button>
                      <img src={resolveMediaUrl(url)} alt={`existing-media-${index + 1}`} className="media-preview-image" />
                    </div>
                  )
                )}
              </div>
            </div>
          )}
          {formData.videoFiles?.length > 0 && (
            <div className="mt-3">
              <h6 className="mb-2">{isEditMode ? "New Creative to Upload" : "Selected Creative"}</h6>
              <div className="preview-panel">
                {formData.videoFiles.map((file, index) => {
                  const url = URL.createObjectURL(file);

                  return file.type.startsWith("video/") ? (
                    <div className="preview-item" key={`${file.name}-${file.lastModified}-${index}`}>
                      <video controls src={url} className="video-preview" />
                      <small className="text-muted d-block mt-1">{file.name}</small>
                    </div>
                  ) : (
                    <div className="preview-item" key={`${file.name}-${file.lastModified}-${index}`}>
                      <img src={url} alt={file.name} className="media-preview-image" />
                      <small className="text-muted d-block mt-1">{file.name}</small>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div className="modal-footer-custom mt-4 pt-3">
            <div className="d-flex gap-2 justify-content-end">
              <Button variant="outline-secondary" onClick={closeManageModal} disabled={submitLoading}>
                <i className="fas fa-times me-2"></i>Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={submitLoading || loadingMachines}>
                {submitLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    {isEditMode ? "Updating..." : "Assigning..."}
                  </>
                ) : (
                  <>
                    <i className={`fas ${isEditMode ? "fa-save" : "fa-check-circle"} me-2`}></i>
                    {isEditMode ? "Update Creative" : "Assign Creative"}
                  </>
                )}
              </Button>
            </div>
          </div>
        </Form>
      </CommonModal>

     <CommonModal
  visible={showViewModal}
  onClose={() => setShowViewModal(false)}
  title={
    <div className="d-flex align-items-center text-white">
      <div className="modal-icon bg-white bg-opacity-20 rounded-circle p-2 me-3">
        <i className="fas fa-eye fa-lg"></i>
      </div>
      <div>
        <h5 className="mb-0 fw-bold">Creative Details</h5>
        <small className="opacity-75">View creative content and machine assignments</small>
      </div>
    </div>
  }
  size="xl"
  showConfirm={false}
  cancelText="Close"
  backdrop="static"
  customHeaderClass="modal-header-gradient"
>
  {viewingAssignment && (
    <div className="creative-details-container">
      {/* Header Section with Creative Info */}
      <div className="creative-header mb-4 p-3 bg-light rounded-3">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <div className="d-flex align-items-center gap-3">
            <h4 className="fw-bold mb-1 text-primary">{viewingAssignment.title}</h4>
              <span className="text-muted">
                <i className="fas fa-clock me-1"></i>
                Uploaded: {new Date(viewingAssignment.uploadedAt).toLocaleDateString()}
              </span>
              {/* <span className="badge bg-info bg-opacity-10 text-info px-3 py-2">
                <i className="fas fa-file me-2"></i>
                {viewingAssignment.fileName}
              </span> */}
            
            </div>
          </div>
          {/* <div className="creative-actions">
            <Button variant="outline-primary" size="sm" className="me-2">
              <i className="fas fa-download me-1"></i> Download
            </Button>
            <Button variant="outline-secondary" size="sm">
              <i className="fas fa-share-alt me-1"></i> Share
            </Button>
          </div> */}
        </div>
      </div>

      {/* Media Gallery Section with Better Styling */}
      {(viewingAssignment.mediaUrls?.length > 0 || viewingAssignment.videoUrl) && (
        <div className="media-gallery-section mb-4">
          <h6 className="fw-semibold mb-3">
            <i className="fas fa-images me-2 text-primary"></i>
            Media Preview
          </h6>
          <div className="row g-3">
            {(viewingAssignment.mediaUrls?.length > 0 ? viewingAssignment.mediaUrls : [viewingAssignment.videoUrl]).map((url, index) => {
              const resolvedUrl = imageUrl(url);
              const isVideo = isVideoMediaUrl(url, viewingAssignment.mediaType);
              
              return (
                <div className="col-md-4 col-lg-3" key={`${viewingAssignment.id || "media"}-${index}`}>
                  <div className="media-preview-card border rounded-3 overflow-hidden shadow-sm">
                    {isVideo ? (
                      <video 
                        controls
                        className="w-100"
                        style={{maxHeight: '200px', objectFit: 'contain', background: '#000'}}
                        src={resolvedUrl}
                        muted
                       autoPlay
                       audio
                      >
                        Your browser does not support the video tag.
                      </video>
                    ) : (
                      <img 
                        src={resolvedUrl} 
                        alt={`${viewingAssignment.title}-${index + 1}`} 
                        className="w-100"
                        style={{maxHeight: '300px', objectFit: 'contain', background: '#f8f9fa'}}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Stats Cards with Gradient Backgrounds */}
      <Row className="g-3 mb-4">
        {/* <Col md={4}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="bg-gradient-primary-light">
              <div className="d-flex align-items-center">
                <div className="stats-icon bg-primary bg-opacity-10 rounded-circle p-3 me-3">
                  <i className="fas fa-file-alt fa-2x text-primary"></i>
                </div>
                <div>
                  <small className="text-muted text-uppercase fw-semibold">File Size</small>
                  <h5 className="mb-0 fw-bold">{formatFileSize(viewingAssignment.fileSize)}</h5>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col> */}
        <Col md={4}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="bg-gradient-success-light">
              <div className="d-flex align-items-center">
                <div className="stats-icon bg-success bg-opacity-10 rounded-circle p-3 me-3">
                  <i className="fas fa-calendar-alt fa-2x text-success"></i>
                </div>
                <div>
                  <small className="text-muted text-uppercase fw-semibold">AssignedOn</small>
                  <h5 className="mb-0 fw-bold">{new Date(viewingAssignment.uploadedAt).toLocaleDateString()}</h5>
                  <small className="text-muted">{new Date(viewingAssignment.uploadedAt).toLocaleTimeString()}</small>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="bg-gradient-info-light">
              <div className="d-flex align-items-center">
                <div className="stats-icon bg-info bg-opacity-10 rounded-circle p-3 me-3">
                  <i className="fas fa-cogs fa-2x text-info"></i>
                </div>
                <div>
                  <small className="text-muted text-uppercase fw-semibold">Total Machines</small>
                  <h5 className="mb-0 fw-bold">{viewingAssignment.machineIds.length}</h5>
                  <small className="text-muted">Assigned units</small>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Machines Section with Improved Badges */}
      <div className="machines-section">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="fw-semibold mb-0">
            <i className="fas fa-robot me-2 text-primary"></i>
            Assigned Machines
          </h6>
          <span className="badge bg-primary px-3 py-2">
            {viewingAssignment.machineIds.length} Machine{viewingAssignment.machineIds.length !== 1 ? 's' : ''}
          </span>
        </div>
        
        {viewingAssignment.machineIds.length > 0 ? (
          <div className="machine-grid">
            {viewingAssignment.machineIds.map((machineId, index) => (
              <div key={`${machineId}-${index}`} className="machine-card p-3 border rounded-3">
                <div className="d-flex align-items-center">
                  <div className="machine-icon bg-success bg-opacity-10 rounded-circle p-2 me-3">
                    <i className="fas fa-microchip text-success"></i>
                  </div>
                  <div className="flex-grow-1">
                    <div className="fw-semibold">{getMachineName(machineId, viewingAssignment.machineNames?.[index])}</div>
                    <small className="text-muted">
                      <i className="fas fa-hashtag me-1"></i>
                      ID: {machineId}
                    </small>
                  </div>
                  <Badge bg="light" text="dark" className="border">
                    <i className="fas fa-check-circle text-success me-1"></i>
                    Active
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-4 bg-light rounded-3">
            <i className="fas fa-robot fa-3x text-muted mb-2"></i>
            <p className="text-muted mb-0">No machines assigned yet</p>
          </div>
        )}
      </div>
    </div>
  )}
</CommonModal>
    </>
  );
}

export default CreativeVideoAssignments;
